import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faChevronUp, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import './Pricing.css';

const Pricing = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [expandedAddOn, setExpandedAddOn] = useState(null);

  const addOns = [
    {
      name: 'Professional Email Setup',
      description: 'Branded email (e.g. hello@yourdomain.com)',
      price: '£25 (one-time setup fee)',
    },
    {
      name: 'Google Analytics Integration',
      description: 'Advanced website analytics and reporting',
      price: '£1/month',
    },
    {
      name: 'Additional Content Updates',
      description: 'Extra 1 hour of content updates per month (in addition to the 1 hour included in your plan). See FAQs for full details.',
      price: '£40/month',
    },
    {
      name: 'Unlimited Support Package',
      description: 'Comprehensive support solution with unlimited updates.',
      price: 'Contact for pricing',
    },
  ];

  const toggleFeatures = (cardIndex) => {
    setExpandedCard(expandedCard === cardIndex ? null : cardIndex);
  };

  const toggleAddOn = (index) => {
    setExpandedAddOn(expandedAddOn === index ? null : index);
  };
  return (
    <section id="pricing" className="pricing section">
      <div className="container">
        <motion.div
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Our All-Inclusive Website Plans</h2>
          <p>Transparent pricing for professionally managed websites. Let us handle the tech, so you can focus on your business.</p>
        </motion.div>



        <div className="pricing-container">
          {/* Essential Plan */}
          <motion.div
            className="pricing-card essential"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="pricing-badge">ESSENTIAL</div>
            <p className="pricing-description">
              Ideal for startups and small businesses needing a professional, hassle-free online presence.
            </p>
            <div className="pricing-price">
              <span className="starting-from">Starting from </span>
              <span className="price">£25</span>/month (inc VAT)
              <div className="contract-info">12 month contract</div>
              <div className="setup-fee">
                Package Setup Fee (one-time): £149
              </div>
            </div>

            <button
              className="features-toggle"
              onClick={() => toggleFeatures(0)}
              aria-expanded={expandedCard === 0}
            >
              <span>{expandedCard === 0 ? 'Hide Features' : 'Show Features'}</span>
              <FontAwesomeIcon
                icon={expandedCard === 0 ? faChevronUp : faChevronDown}
                className="toggle-icon"
              />
            </button>

            <ul className={`pricing-features ${expandedCard === 0 ? 'expanded' : ''}`}>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Mobile-friendly design (1–3 pages)</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> 1 hour of content updates per month</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Admin panel to edit text and images</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Hosting and SSL included</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Contact form setup</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Delivered in 3–5 days</li>
            </ul>
            <Link to="/contact" className="pricing-button">Choose Essential</Link>
            <p className="pricing-footer">
              Best for simple, low-maintenance websites with no dynamic features.
            </p>
          </motion.div>

          {/* Business Plan */}
          <motion.div
            className="pricing-card business"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="pricing-badge business">BUSINESS</div>
            <p className="pricing-description">
              For growing businesses requiring more pages and enhanced features.
            </p>
            <div className="pricing-price">
              <span className="starting-from">Starting from </span>
              <span className="price">£35</span>/month (inc VAT)
              <div className="contract-info">12 month contract</div>
              <div className="setup-fee">
                Package Setup Fee (one-time): £249
              </div>
            </div>

            <button
              className="features-toggle"
              onClick={() => toggleFeatures(1)}
              aria-expanded={expandedCard === 1}
            >
              <span>{expandedCard === 1 ? 'Hide Features' : 'Show Features'}</span>
              <FontAwesomeIcon
                icon={expandedCard === 1 ? faChevronUp : faChevronDown}
                className="toggle-icon"
              />
            </button>

            <ul className={`pricing-features ${expandedCard === 1 ? 'expanded' : ''}`}>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Up to 7 custom-designed pages</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> 1 hour of content updates per month</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Services or blog section</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Google Maps integration</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> WhatsApp chat</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Booking system included</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> SEO-ready setup</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Hosting and SSL included</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Admin panel for content updates</li>
            </ul>
            <Link to="/contact" className="pricing-button secondary-button">Choose Business</Link>
            <p className="pricing-footer">
              Designed for service-based businesses that need bookings and regular content updates.
            </p>
          </motion.div>

          {/* E-commerce Plan */}
          <motion.div
            className="pricing-card enterprise"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="pricing-badge tertiary">E-COMMERCE</div>
            <p className="pricing-description">
              Tailored solutions for established e-commerce businesses with unique, complex, or large-scale requirements.
            </p>
            <div className="pricing-price">
              <span className="starting-from">Starting from </span>
              <span className="price">£45</span>/month (inc VAT)
              <div className="contract-info">12 month contract</div>
              <div className="setup-fee">
                Package Setup Fee (one-time): £399
              </div>
            </div>

            <button
              className="features-toggle"
              onClick={() => toggleFeatures(2)}
              aria-expanded={expandedCard === 2}
            >
              <span>{expandedCard === 2 ? 'Hide Features' : 'Show Features'}</span>
              <FontAwesomeIcon
                icon={expandedCard === 2 ? faChevronUp : faChevronDown}
                className="toggle-icon"
              />
            </button>

            <ul className={`pricing-features ${expandedCard === 2 ? 'expanded' : ''}`}>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Sell products online without commission fees</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> 1 hour of content updates per month</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Full-featured online store</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Product, stock, and order management</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> PayPal and Stripe integration</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Discount codes</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> SEO-ready setup</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Hosting and SSL included</li>
            </ul>
            <Link to="/contact" className="pricing-button tertiary-button">Choose E-commerce</Link>
            <p className="pricing-footer">
              Supports product catalogs up to ~100 items. For larger stores or higher traffic, we recommend a performance hosting upgrade.
            </p>
          </motion.div>
        </div>

        {/* Budget Package - Less Conspicuous Option */}
        <motion.div
          className="budget-package"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="budget-package-content">
            <h4>Budget-Friendly Option</h4>
            <p>Same great features as our Essential plan, but without content updates included. No setup fee.</p>
            <div className="budget-price">
              <span className="starting-from">Starting from </span>
              <span className="price">£15</span>/month (inc VAT)
              <div className="contract-info">12 month contract</div>
            </div>
            <div className="budget-action">
              <Link to="/contact" className="pricing-button budget-button">Choose Budget</Link>
            </div>
          </div>
        </motion.div>

        <div className="managed-services-grid">
          <div className="service-item">
            <h4>Fully Managed Plans</h4>
            <p>We handle all technical aspects including hosting, security and maintenance. All main plans include 1 hour of content updates per month, with additional content management available through our Optional Boosters.</p>
          </div>
          <div className="service-item">
            <h4>DIY CMS Content Management</h4>
            <p>We build your site with an accessible CMS, enabling you to update text, images, pages, menus, and blog posts directly, so you don't have to wait for developers to complete your content updates.</p>
          </div>
          <div className="service-item">
            <h4>CMS Training After Launch</h4>
            <p>Personalised onboarding session, user guide, and checklist — covering everything you need to confidently manage your own site.</p>
          </div>

        </div>

        <div className="add-ons-section">
          <div className="section-title">
            <h2>Optional Boosters</h2>
            <p>Only pay for what you need. Add features as your business grows:</p>
          </div>

          <div className="add-ons-table">
            <table>
              <thead>
                <tr>
                  <th>Add-On</th>
                  <th>Description</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {addOns.map((addOn, index) => (
                  <tr key={index} className={expandedAddOn === index ? 'expanded' : ''} onClick={() => toggleAddOn(index)}>
                    <td data-label="Add-On">
                      <div className="addon-main-row">
                        {addOn.name}
                        <button className="addon-toggle" >
                          <FontAwesomeIcon icon={expandedAddOn === index ? faMinus : faPlus} />
                        </button>
                      </div>
                    </td>
                    {/* Responsive: stack description and price vertically on mobile when expanded */}
                    {expandedAddOn === index ? (
                      <td colSpan={2} className="addon-details-stacked">
                        <div className="addon-description-stacked">{addOn.description}</div>
                        <div className="addon-price-stacked">{addOn.price}</div>
                      </td>
                    ) : (
                      <>
                        <td data-label="Description" className="addon-description">{addOn.description}</td>
                        <td data-label="Price" className="addon-price">{addOn.price}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="pricing-footer">All our prices are inclusive of VAT</p>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Pricing;
